import Link from "next/link";
import SectionHeader from "@/components/SectionHeader";
import { euro } from "@/utils/preisAnalyse";

/**
 * Text unter der Vergleichsliste.
 *
 * Die Seite hatte 711 Wörter und keine einzige H2 — nur Überschrift, Filter
 * und Liste. Für ein Werkzeug ist das richtig, für eine Seite, die zu
 * "fotobox vergleich" ranken soll, zu wenig: Es fehlte jede Erklärung, wonach
 * hier eigentlich verglichen wird.
 *
 * Der Text steht bewusst UNTER der Liste. Wer vergleichen will, soll nicht
 * erst an einem Aufsatz vorbeiscrollen.
 */
export default function VergleichInhalt({ analyse, merkmale, stand }) {
  if (!analyse) return null;

  const anteil = (n) => `${n} von ${merkmale.anzahlBoxen}`;

  return (
    <div className="space-y-12 pt-16 lg:space-y-16">
      <SectionHeader
        id="kriterien"
        title="Wonach wir vergleichen"
        subtitle="Dieselben Kriterien für jede Fotobox — unabhängig davon, wer sie vermietet."
      >
        <div className="mx-auto mt-8 max-w-3xl text-left text-[15px] leading-7 text-slate-700">
          <p>
            Im Vergleich stehen {merkmale.anzahlBoxen} Fotoboxen von{" "}
            {merkmale.anzahlAnbieter} Anbietern mit {analyse.anzahlPakete}{" "}
            Paketen. Die Angaben pflegen die Anbieter selbst; bewertet werden
            sie nach festen Regeln.
          </p>
          <dl className="mt-6 space-y-4">
            <div className="border-l-2 border-slate-200 pl-5">
              <dt className="font-semibold text-slate-900">
                Preis — immer als Gesamtpreis
              </dt>
              <dd className="mt-1 text-sm leading-6 text-slate-600">
                Grundpreis plus Paket, nicht der Lockpreis. Bei Anbietern vor
                Ort kommt die Anfahrt für deine Postleitzahl dazu, sobald du sie
                eingibst. Die Pakete reichen von {euro(analyse.gesamt.min)} € bis{" "}
                {euro(analyse.gesamt.max)} €, die Hälfte liegt unter{" "}
                {euro(analyse.gesamt.median)} €.
              </dd>
            </div>
            <div className="border-l-2 border-slate-200 pl-5">
              <dt className="font-semibold text-slate-900">
                Bildqualität — Kamera und Licht getrennt
              </dt>
              <dd className="mt-1 text-sm leading-6 text-slate-600">
                Eine Spiegelreflexkamera bekommt dieselbe Note, egal von wem sie
                kommt ({anteil(merkmale.spiegelreflex)} haben eine). Beim Licht
                zählt, ob geblitzt wird — {anteil(merkmale.studioblitz)} tun das.
              </dd>
            </div>
            <div className="border-l-2 border-slate-200 pl-5">
              <dt className="font-semibold text-slate-900">
                Druck — Kapazität statt Versprechen
              </dt>
              <dd className="mt-1 text-sm leading-6 text-slate-600">
                Entscheidend ist, wie viele Bilder das Papier hergibt. Wir
                vergleichen die Kapazität mit deiner Gästezahl und weisen aus,
                wo eine Flatrate die Rechnung erübrigt.
              </dd>
            </div>
            <div className="border-l-2 border-slate-200 pl-5">
              <dt className="font-semibold text-slate-900">
                Lieferung — Weg und Frist
              </dt>
              <dd className="mt-1 text-sm leading-6 text-slate-600">
                Versand oder Aufbau vor Ort, und bei einem gewählten Termin der
                konkrete Tag, an dem die Box ankommt — samt Hinweis, wenn es zu
                knapp wird.
              </dd>
            </div>
          </dl>
          <p className="mt-6">
            Ausführlicher steht das auf der{" "}
            <Link
              href="/#methodik"
              className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
            >
              Seite über unsere Methodik
            </Link>{" "}
            — inklusive dem, was wir nicht tun und woran wir verdienen.
          </p>
        </div>
      </SectionHeader>

      <SectionHeader
        id="so-filtern"
        title="So kommst du am schnellsten zum passenden Angebot"
        subtitle="Drei Filter bringen mehr als zehn."
      >
        <div className="mx-auto mt-8 max-w-3xl text-left text-[15px] leading-7 text-slate-700">
          <ol className="list-decimal space-y-3 pl-5 marker:font-semibold marker:text-slate-400">
            <li>
              <strong className="font-semibold text-slate-900">Gästezahl</strong>{" "}
              zuerst. Sie entscheidet, ob der Drucker reicht — der häufigste
              Grund für Ärger am Eventabend.
            </li>
            <li>
              <strong className="font-semibold text-slate-900">
                Versand oder vor Ort
              </strong>
              . Das ist die eigentliche Weggabelung: Versandboxen sind
              günstiger, Anbieter vor Ort nehmen dir den Aufbau ab. Mit
              Postleitzahl siehst du sofort, wer überhaupt liefert.
            </li>
            <li>
              <strong className="font-semibold text-slate-900">
                Druck oder digital
              </strong>
              . Ohne Drucker wird es deutlich günstiger — dafür nimmt niemand
              etwas mit nach Hause.
            </li>
          </ol>
          <p className="mt-6">
            Alles Weitere — Requisiten, Hintergrund, Online-Galerie — ist bei
            den meisten Anbietern ohnehin dabei und taugt selten als
            Ausschlusskriterium.
          </p>
          <p className="mt-6 text-xs text-slate-500">
            Alle Zahlen aus dem Katalog, Stand {stand}.
          </p>
        </div>
      </SectionHeader>
    </div>
  );
}
